import type { EstadoTransporte } from '../produtos'

/**
 * Perfil de dimensões do caminhão-tanque didático.
 *
 * Mesma ideia do perfil de veículo do CSV Lab: o 3D é montado a partir destes
 * números, nunca de números soltos em JSX, e as oficinas (transbordo, BLEVE)
 * leem a mesma geometria — a válvula que o 3D desenha é a válvula cuja fase
 * (líquido ou vapor) o transbordo calcula.
 *
 * Referencial do caminhão: frente para +Z, para cima +Y, lado esquerdo
 * (motorista) para +X. A origem fica no chão, no meio do conjunto.
 *
 * As dimensões são representativas de semirreboques rodoviários brasileiros,
 * arredondadas para ensino — não são de um fabricante.
 */

export type SecaoTanque = 'circular' | 'eliptica'

export type TipoAbertura =
  | 'valvulaFaseLiquida'
  | 'valvulaFaseVapor'
  | 'valvulaFundo'
  | 'valvulaAlivio'
  | 'bocaVisita'

export interface AberturaTanque {
  readonly id: string
  readonly nome: string
  readonly tipo: TipoAbertura
  /**
   * Onde a abertura se comunica com o interior do tanque, ao longo do eixo:
   * metros a partir do centro do tanque, positivo para a frente.
   */
  readonly zM: number
  /**
   * Ângulo na seção, em torno do eixo do tanque: 0 = topo, π = fundo,
   * +π/2 = lado esquerdo (+X). É o ponto INTERNO da abertura: a linha de fase
   * vapor de um tanque de GLP sai embaixo, no armário de válvulas, mas
   * capta no topo por tubo pescador — e é a captação que decide a fase.
   */
  readonly anguloRad: number
  readonly descricao: string
}

export interface PerfilTanque {
  readonly id: 'pressurizado' | 'atmosferico'
  readonly nome: string
  readonly secao: SecaoTanque
  /** Comprimento do costado cilíndrico, sem os tampos. */
  readonly comprimentoM: number
  /** Eixo horizontal da seção (diâmetro, se circular). */
  readonly larguraM: number
  /** Eixo vertical da seção. */
  readonly alturaM: number
  /** Profundidade de cada tampo (calota), além do costado. */
  readonly profundidadeTampoM: number
  /** Altura do eixo do tanque acima do chão, com o caminhão sobre rodas. */
  readonly alturaEixoM: number
  /** Centro do tanque ao longo de Z, no referencial do caminhão. */
  readonly zCentroM: number
  readonly compartimentos: number
  readonly aberturas: readonly AberturaTanque[]
  /** Para quais estados de transporte este tanque serve. */
  readonly estados: readonly EstadoTransporte[]
}

export interface PerfilCavalo {
  readonly comprimentoM: number
  readonly larguraM: number
  readonly alturaM: number
  /** Centro do cavalo ao longo de Z. */
  readonly zCentroM: number
}

export interface PerfilCaminhaoTanque {
  readonly cavalo: PerfilCavalo
  readonly tanque: PerfilTanque
}

// O chassi traseiro se sobrepõe ao semirreboque; a cabine mantém folga para articulação.
const CAVALO: PerfilCavalo = { comprimentoM: 6.2, larguraM: 2.5, alturaM: 3.6, zCentroM: 3.2 }

/** Carreta-tanque de gás liquefeito (GLP, amônia, cloro): seção circular, um compartimento. */
export const TANQUE_PRESSURIZADO: PerfilTanque = {
  id: 'pressurizado',
  nome: 'Tanque pressurizado (gás liquefeito)',
  secao: 'circular',
  comprimentoM: 11.6,
  larguraM: 2.4,
  alturaM: 2.4,
  profundidadeTampoM: 0.6,
  alturaEixoM: 2.05,
  zCentroM: -3.2,
  compartimentos: 1,
  estados: ['gasLiquefeitoPressurizado', 'gasComprimido'],
  aberturas: [
    {
      id: 'liquido',
      nome: 'Válvula de fase líquida',
      tipo: 'valvulaFaseLiquida',
      zM: -1.0,
      anguloRad: Math.PI,
      descricao: 'Capta no fundo do tanque: com o tanque em pé, entrega líquido.',
    },
    {
      id: 'vapor',
      nome: 'Válvula de fase vapor',
      tipo: 'valvulaFaseVapor',
      zM: -1.6,
      anguloRad: 0,
      descricao: 'Capta no topo por tubo pescador: com o tanque em pé, entrega vapor. É a linha de retorno de vapor no transbordo.',
    },
    {
      id: 'alivio',
      nome: 'Válvula de alívio de pressão',
      tipo: 'valvulaAlivio',
      zM: 0.4,
      anguloRad: 0,
      descricao: 'Abre quando a pressão interna passa do ajuste. Precisa estar em fase vapor para funcionar como projetada.',
    },
    {
      id: 'bocaVisita',
      nome: 'Boca de visita',
      tipo: 'bocaVisita',
      zM: 2.6,
      anguloRad: 0,
      descricao: 'Acesso ao interior para inspeção, fechado com tampa aparafusada.',
    },
  ],
}

/** Carreta-tanque de combustível líquido: seção elíptica, cinco compartimentos. */
export const TANQUE_ATMOSFERICO: PerfilTanque = {
  id: 'atmosferico',
  nome: 'Tanque de líquido (combustível, produto químico)',
  secao: 'eliptica',
  comprimentoM: 11.8,
  larguraM: 2.5,
  alturaM: 1.9,
  profundidadeTampoM: 0.35,
  alturaEixoM: 1.85,
  zCentroM: -3.2,
  compartimentos: 5,
  estados: ['liquido'],
  aberturas: Array.from({ length: 5 }, (_, i) => {
    const largura = 11.8 / 5
    const zM = -11.8 / 2 + largura * (i + 0.5)
    return [
      {
        id: `fundo${i + 1}`,
        nome: `Válvula de fundo — compartimento ${i + 1}`,
        tipo: 'valvulaFundo' as const,
        zM,
        anguloRad: Math.PI,
        descricao: 'Descarga por gravidade, no ponto mais baixo do compartimento.',
      },
      {
        id: `boca${i + 1}`,
        nome: `Boca de visita — compartimento ${i + 1}`,
        tipo: 'bocaVisita' as const,
        zM,
        anguloRad: 0,
        descricao: 'Carregamento pelo topo e acesso; tem válvula de respiro/alívio na tampa.',
      },
    ]
  }).flat(),
}

export function perfilParaEstado(estado: EstadoTransporte): PerfilCaminhaoTanque {
  const tanque = TANQUE_PRESSURIZADO.estados.includes(estado) ? TANQUE_PRESSURIZADO : TANQUE_ATMOSFERICO
  return { cavalo: CAVALO, tanque }
}

/** Semieixos da seção (horizontal, vertical). */
export const semieixos = (tanque: PerfilTanque): readonly [number, number] => [tanque.larguraM / 2, tanque.alturaM / 2]

/**
 * Volume interno do tanque, m³: costado (cilindro de seção elíptica) mais dois
 * tampos tratados como meio elipsoide cada.
 */
export function volumeTanqueM3(tanque: PerfilTanque): number {
  const [a, b] = semieixos(tanque)
  const costado = Math.PI * a * b * tanque.comprimentoM
  const tampos = 2 * ((2 / 3) * Math.PI * a * b * tanque.profundidadeTampoM)
  return costado + tampos
}

/** Ponto da abertura no referencial do caminhão (tanque sobre rodas). */
export function pontoDaAbertura(tanque: PerfilTanque, abertura: AberturaTanque): readonly [number, number, number] {
  const [a, b] = semieixos(tanque)
  return [
    Math.sin(abertura.anguloRad) * a,
    tanque.alturaEixoM + Math.cos(abertura.anguloRad) * b,
    tanque.zCentroM + abertura.zM,
  ]
}

/** Comprimento total do conjunto, para câmera e cenas. */
export function comprimentoConjuntoM(perfil: PerfilCaminhaoTanque): number {
  const frente = perfil.cavalo.zCentroM + perfil.cavalo.comprimentoM / 2
  const tras = perfil.tanque.zCentroM - perfil.tanque.comprimentoM / 2 - perfil.tanque.profundidadeTampoM - 0.5
  return frente - tras
}
